import { type Dispatch, type SetStateAction, useMemo } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { noop } from "@common/helpers/function";
import type { GetTransactionsResponse } from "@common/schemas/transaction.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import type { PaginationMode } from "@hooks/paginationMode";
import { useLoadMoreTableState } from "@hooks/useLoadMoreTableState";
import type { TransactionsLoadMore } from "./PaginatedTransactionsTable.context";
import {
    type UseTransactionsQueryParam,
    useTransactionsQuery,
} from "./useTransactionsQuery";
import { useTransactionsSnapshotRows } from "./useTransactionsSnapshotRows";

type ListQueryParams = Pick<
    UseTransactionsQueryParam,
    | "filters"
    | "sort"
    | "includeMatchDetails"
    | "includeAccrualMatchDetails"
    | "includeAccrualMatchSuggestions"
>;

interface UseTransactionsListRowsParams extends ListQueryParams {
    paginationMode: PaginationMode;
    /** Suggested-match lists; numbered pages only. */
    matchingTransactionId?: number;
    queryKeyPrefix?: string;
}

interface TransactionsListRows {
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    data: GetTransactionsResponse | undefined;
    isFetching: boolean;
    /** Cache entries holding the shown rows, for writers that patch them. */
    cacheKeys: QueryKey[];
    loadMore?: TransactionsLoadMore;
}

/**
 * The transactions a list provider shows: one numbered page at a time, or
 * every batch loaded so far from the id snapshot when the list loads more on
 * scroll.
 */
export function useTransactionsListRows({
    paginationMode,
    matchingTransactionId,
    queryKeyPrefix,
    ...listQueryParams
}: UseTransactionsListRowsParams): TransactionsListRows {
    const isPagesMode = paginationMode.type === "pages";
    const page = paginationMode.type === "pages" ? paginationMode.page : 1;
    const setPage =
        paginationMode.type === "pages" ? paginationMode.setPage : noop;
    const workspaceId = useActiveWorkspace()?.id ?? "";

    const pagedQuery = useTransactionsQuery(
        {
            ...listQueryParams,
            page,
            matchingTransactionId,
            skipCount: true,
            queryKeyPrefix,
        },
        { enabled: isPagesMode },
    );

    const snapshotRows = useTransactionsSnapshotRows({
        ...listQueryParams,
        workspaceId,
        queryKeyPrefix,
        enabled: !isPagesMode,
    });

    const { rows, isTruncated } = snapshotRows;
    const loadedData = useMemo(
        () => (rows === undefined ? undefined : { data: rows }),
        [rows],
    );
    const tableState = useLoadMoreTableState(snapshotRows);
    const loadMore = useMemo<TransactionsLoadMore>(
        () => ({ ...tableState, isTruncated }),
        [tableState, isTruncated],
    );

    if (isPagesMode) {
        return {
            page,
            setPage,
            data: pagedQuery.query.data,
            isFetching: pagedQuery.query.isFetching,
            cacheKeys: [pagedQuery.queryKey],
        };
    }
    return {
        page,
        setPage,
        data: loadedData,
        isFetching: snapshotRows.isFirstPageFetching,
        cacheKeys: snapshotRows.loadedQueryKeys,
        loadMore,
    };
}
